import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Box, Text } from '@rsos/base-components';
import { LayoutContext } from '@rsos/components/capstone/base/Layout';
import type { RootState } from '../../../app/rootReducer';
import {
  IconWrapper,
  ToolTip,
  TooltipChild,
  TooltipPortal,
} from './ActionBar.styles';

export const DEFAULT_ICON_WIDTH = 17;
const ADDITIONAL_SPACING = 2;

interface ActionBarItemProps {
  children?: ReactNode;
  iconWidth?: string | number;
  onClick?: () => void;
  shouldRender?: boolean;
  tooltipText?: ReactNode;
  isActive?: boolean;
  color?: string;
  iconDataName?: string;
}

export const ActionBarItem = ({
  children = null,
  iconWidth = DEFAULT_ICON_WIDTH,
  onClick,
  shouldRender = true,
  tooltipText = '',
  isActive = false,
  color = 'secondaryLink',
  iconDataName = 'action-bar-item-icon',
  ...props
}: ActionBarItemProps) => {
  const toolTipRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const { layout } = useContext(LayoutContext);
  const isQueueCollapsed = useSelector(
    (state: RootState) => state.queue?.isQueueCollapsed,
  );
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
    placement: string;
  }>({
    top: 0,
    left: 0,
    placement: 'top',
  });

  const [compensation, setCompensation] = useState(0);

  const updateTooltipPosition = useCallback(() => {
    if (!buttonRef.current || !toolTipRef.current) {
      return;
    }

    const tooltipWidth = toolTipRef.current.offsetWidth;
    const tooltipHeight = toolTipRef.current.offsetHeight;
    const viewportWidth = layout?.body?.width || window.innerWidth;
    const viewportHeight = window.innerHeight;
    const buttonRect = buttonRef.current.getBoundingClientRect();

    if (isQueueCollapsed) {
      const unclampedLeft = buttonRect.right + ADDITIONAL_SPACING;
      const left = Math.min(
        unclampedLeft,
        viewportWidth - tooltipWidth - ADDITIONAL_SPACING,
      );
      const top = Math.min(
        Math.max(buttonRect.top + buttonRect.height / 2, tooltipHeight / 2 + 4),
        viewportHeight - tooltipHeight / 2 - 4,
      );

      setTooltipPosition({ top, left, placement: 'right' });
      setCompensation(0);
      return;
    }

    const defaultCenter = buttonRect.left + buttonRect.width / 2;
    const minCenter = tooltipWidth / 2 + ADDITIONAL_SPACING;
    const maxCenter = viewportWidth - tooltipWidth / 2 - ADDITIONAL_SPACING;
    const centerX = Math.min(Math.max(defaultCenter, minCenter), maxCenter);
    const top = Math.max(
      ADDITIONAL_SPACING,
      buttonRect.top - tooltipHeight - ADDITIONAL_SPACING,
    );

    const tooltipRight = defaultCenter + tooltipWidth / 2;
    const rightOverlap = tooltipRight - viewportWidth;
    setCompensation(rightOverlap > 0 ? rightOverlap + ADDITIONAL_SPACING : 0);

    setTooltipPosition({ top, left: centerX, placement: 'top' });
  }, [isQueueCollapsed, layout?.body?.width]);

  useLayoutEffect(() => {
    if (!tooltipText) return;
    updateTooltipPosition();
  }, [tooltipText, updateTooltipPosition]);

  useLayoutEffect(() => {
    if (!isTooltipVisible) return;
    updateTooltipPosition();
  }, [isTooltipVisible, layout?.body?.width, updateTooltipPosition]);

  useEffect(() => {
    if (!isTooltipVisible) return;

    const handleScrollOrResize = () => {
      updateTooltipPosition();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isTooltipVisible, updateTooltipPosition]);

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      <Box
        data-name="action-bar-item"
        onClick={onClick}
        pb={3}
        pl={3}
        {...props}
      >
        <ToolTip
          ref={buttonRef}
          onMouseEnter={() => setIsTooltipVisible(true)}
          onMouseLeave={() => setIsTooltipVisible(false)}
          onFocus={() => setIsTooltipVisible(true)}
          onBlur={() => setIsTooltipVisible(false)}
        >
          {tooltipText && (
            <TooltipChild
              data-name="action-bar-item-tooltip"
              ref={toolTipRef}
              overlapCompensation={compensation}
              aria-hidden
            >
              <Text size="small">{tooltipText}</Text>
            </TooltipChild>
          )}
          <IconWrapper
            isActive={isActive}
            data-name={iconDataName}
            iconWidth={iconWidth}
            color={color}
          >
            {children}
          </IconWrapper>
        </ToolTip>
      </Box>
      {tooltipText &&
        isTooltipVisible &&
        typeof document !== 'undefined' &&
        createPortal(
          <TooltipPortal
            data-name="action-bar-item-tooltip-portal"
            placement={tooltipPosition.placement}
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
          >
            <Text size="small">{tooltipText}</Text>
          </TooltipPortal>,
          document.body,
        )}
    </>
  );
};
